import { Button, Col, Form as ReactForm, Row } from 'react-bootstrap';
import { SalvButton, SalvModal } from '@salvapp/ui-components';
import React, { Fragment, useState } from 'react';
import { ErrorMessage, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../store/store';
import FormField from '../form/FormField';
import { fetchNotes, fetchPerson } from './amlActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/pro-solid-svg-icons';
import { getUserId } from '../auth/authReducer';
import { FormattedSnoozeConfiguration, isCounterparty } from '../common/helper';
import { personStatusInternalApi, snoozeInternalApi } from '../apis';
import { FormikState } from 'formik/dist/types';
import Has from '../auth/Has';
import { CAN_MODIFY_PERSON } from '../auth/permissions';
import dayjs from 'dayjs';
import { InternalApiPersonStatusEnum, InternalApiPersonStatusStatusEnum } from '../api-internal';
import { parseErrorMessage } from '../common/errors/helpers';
import utc from 'dayjs/plugin/utc';
import { ContentPreviewer } from '../common/ContentPreviewer';

dayjs.extend(utc);

const validationSchema = Yup.object().shape({
  status: Yup.object()
    .shape({
      key: Yup.string(),
      value: Yup.string(),
      label: Yup.string(),
    })
    .required('Required'),
  periodSnooze: Yup.boolean(),
  days: Yup.number().when('periodSnooze', {
    is: true,
    then: (schema) => schema.required('Insert number of days to snooze'),
  }),
  amountSnooze: Yup.boolean(),
  totalAmount: Yup.number().when('amountSnooze', {
    is: true,
    then: (schema) => schema.required('Insert total amount of turnover limit'),
  }),
  reason: Yup.string().when(['periodSnooze', 'amountSnooze'], {
    is: (periodSnooze: boolean, amountSnooze: boolean) => periodSnooze || amountSnooze,
    then: (schema) => schema.required('Required'),
  }),
});

export interface PersonUpdateStatusModalFormValues {
  personId: string;
  status: { key: string; value: InternalApiPersonStatusEnum; label: string };
  periodSnooze: boolean;
  days: string;
  amountSnooze: boolean;
  totalAmount: string;
  reason: string;
}

export const PersonUpdateStatusModal = () => {
  const dispatch = useDispatch();
  const person = useSelector((state: AppState) => state.aml.person);
  const userId = useSelector((state: AppState) => getUserId(state));
  const [showModal, setShowModal] = useState(false);
  const [showReason, setShowReason] = useState(false);

  if (person == null || person.id == null) {
    return null;
  }

  const statuses: {
    label: string;
    value: InternalApiPersonStatusEnum;
    key: string;
  }[] = (Object.keys(InternalApiPersonStatusEnum) as (keyof typeof InternalApiPersonStatusEnum)[])
    .filter((key) => isNaN(Number(key)))
    .map((key) => {
      return {
        key: key,
        value: InternalApiPersonStatusEnum[key] as InternalApiPersonStatusEnum,
        label: InternalApiPersonStatusEnum[key],
      };
    });

  const initialValues = {
    personId: person.id,
    status: statuses.filter((option) => option.value === person.status)[0],
    periodSnooze: false,
    days: '',
    amountSnooze: false,
    totalAmount: '',
    reason: '',
  };

  const initialFormikState: Partial<FormikState<PersonUpdateStatusModalFormValues>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };

  const createSnoozeConfiguration = async (values: PersonUpdateStatusModalFormValues) => {
    const createdTime = dayjs().utc().toDate();
    const config = {
      createdBy: { id: userId ? userId : 0 },
      createdTime,
      endTime: values.periodSnooze ? dayjs(createdTime).add(Number(values.days), 'days').toDate() : undefined,
      totalAmount: values.amountSnooze ? Number(values.totalAmount) : undefined,
      note: values.reason,
    };
    await snoozeInternalApi.addSnoozeConfigurations({
      personId: person.id,
      internalApiSnoozeConfig: [config],
    });
  };

  const submit = async (
    values: PersonUpdateStatusModalFormValues,
    { setStatus, setSubmitting, resetForm }: FormikHelpers<PersonUpdateStatusModalFormValues>
  ) => {
    try {
      if (person == null || person.id == null) {
        return null;
      }

      await personStatusInternalApi.updatePersonStatus({
        personId: values.personId,
        internalApiPersonStatus: { status: InternalApiPersonStatusStatusEnum[values.status.value] },
      });
      if (values.periodSnooze || values.amountSnooze) {
        await createSnoozeConfiguration(values);
        dispatch(fetchNotes(person.id));
      }
      dispatch(fetchPerson(person.id));

      setShowModal(false);
      resetForm(initialFormikState);
    } catch (e) {
      setStatus(await parseErrorMessage(e));
    }
    setSubmitting(false);
  };

  const personCategory = () => (isCounterparty(person) ? 'counterparty' : 'customer');

  return (
    <Formik initialValues={initialValues} onSubmit={submit} validationSchema={validationSchema}>
      {({
        isSubmitting,
        values,
        handleChange,
        touched,
        errors,
        setFieldValue,
      }: FormikProps<PersonUpdateStatusModalFormValues>) => {
        return (
          <React.Fragment>
            <Has allPermissions={CAN_MODIFY_PERSON}>
              <Button variant='secondary' className='mx-2' onClick={() => setShowModal(true)}>
                <FontAwesomeIcon icon={faUser} className='fe me-2' />
                Set {personCategory()} status
              </Button>
            </Has>
            <SalvModal show={showModal} onHide={() => setShowModal(false)}>
              <Form>
                <SalvModal.Header closeButton>Set {personCategory()} status</SalvModal.Header>
                <SalvModal.Body>
                  <Row>
                    <Col md={12}>
                      <FormField id={'status-select'} name='status' label='Status' type='select' options={statuses} />
                    </Col>
                  </Row>
                  {person?.snoozeConfiguration ? (
                    <div className='snooze-config-info'>
                      <ContentPreviewer>
                        <FormattedSnoozeConfiguration snoozeConfig={person.snoozeConfiguration} />
                        {person.snoozeConfiguration.note}
                      </ContentPreviewer>
                    </div>
                  ) : (
                    <Fragment>
                      <Row>
                        <ReactForm.Group className='form-group mt-2'>
                          <ReactForm.Check className='form-check form-switch'>
                            <ReactForm.Check.Input
                              id='periodSnooze'
                              name='periodSnooze'
                              onChange={() => {
                                setFieldValue('periodSnooze', !values.periodSnooze);
                                setShowReason(!values.periodSnooze || values.amountSnooze);
                              }}
                              type='checkbox'
                              checked={values.periodSnooze}
                              className='form-check-input'
                            />
                            <ReactForm.Check.Label htmlFor='periodSnooze' className='form-check-label'>
                              Snooze alerts for this {personCategory()} for
                            </ReactForm.Check.Label>
                          </ReactForm.Check>
                        </ReactForm.Group>
                        <ReactForm.Control
                          className='small-number-control'
                          type='text'
                          name='days'
                          value={values.days}
                          onChange={handleChange}
                          isInvalid={touched.days && !!errors.days}
                        />
                        <ReactForm.Label className='mt-2'>days</ReactForm.Label>
                        <ErrorMessage
                          name='days'
                          render={(message) => (
                            <ReactForm.Control.Feedback type='invalid'>{message}</ReactForm.Control.Feedback>
                          )}
                        />
                      </Row>
                      <Row>
                        <ReactForm.Group className='form-group mt-2'>
                          <ReactForm.Check className='form-check form-switch'>
                            <ReactForm.Check.Input
                              id='amountSnooze'
                              name='amountSnooze'
                              onChange={() => {
                                setFieldValue('amountSnooze', !values.amountSnooze);
                                setShowReason(values.periodSnooze || !values.amountSnooze);
                              }}
                              type='checkbox'
                              checked={values.amountSnooze}
                              className='form-check-input'
                            />
                            <ReactForm.Check.Label htmlFor='amountSnooze' className='form-check-label'>
                              Snooze alerts for this {personCategory()} for
                            </ReactForm.Check.Label>
                          </ReactForm.Check>
                        </ReactForm.Group>
                        <ReactForm.Control
                          className='small-number-control'
                          type='text'
                          name='totalAmount'
                          value={values.totalAmount}
                          onChange={handleChange}
                          isInvalid={touched.totalAmount && !!errors.totalAmount}
                        />
                        <ReactForm.Label className='mt-2'>turnover limit</ReactForm.Label>
                        <ErrorMessage
                          name='totalAmount'
                          render={(message) => (
                            <ReactForm.Control.Feedback type='invalid'>{message}</ReactForm.Control.Feedback>
                          )}
                        />
                      </Row>
                      {showReason && (
                        <Row>
                          <Col md={12}>
                            <FormField
                              type='textarea'
                              name='reason'
                              rows={5}
                              label=''
                              placeholder='Enter reason for snoozing...'
                            />
                          </Col>
                        </Row>
                      )}
                      {person &&
                        person.scenarioSnoozeConfigurations &&
                        person.scenarioSnoozeConfigurations.length > 0 && (
                          <Row>
                            <Col md={12}>
                              <div
                                data-testid='snooze-config'
                                className='col snooze-config-info mt-2 content-preview-prefix'
                              >
                                {`This customer has ${person.scenarioSnoozeConfigurations.length} scenario${
                                  person.scenarioSnoozeConfigurations.length > 1 ? 's' : ''
                                } separately snoozed`}
                              </div>
                            </Col>
                          </Row>
                        )}
                    </Fragment>
                  )}
                </SalvModal.Body>

                <SalvModal.Footer>
                  <SalvButton label='Close' variant='muted' onClick={() => setShowModal(false)} />
                  <SalvButton disabled={isSubmitting} type='submit' label='Save' variant='primary' />
                </SalvModal.Footer>
              </Form>
            </SalvModal>
          </React.Fragment>
        );
      }}
    </Formik>
  );
};

export default PersonUpdateStatusModal;
